import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOrgGroups } from 'features/user-group/user-group-list/api/useOrgGroups';
import { IMemberGroup, IMemberTeam } from 'features/members/models';
import {
  fetchMemberGroups,
  loadGroupsFromCache,
  saveGroupsToCache,
} from './useMembersData/useMembersData';
import { useAuthenticatedAxios } from 'features/common/apiUtils/AxiosProvider';
import { identity as identityConfig } from '@sitecore-ui/portal-singular';
import { env } from 'features/common/config/envConfig';
import { IGetMembersResponse } from 'features/members/services/identity/membersModel';
import {
  normalizeMemberId,
  toPrefixedMemberId,
} from 'features/members/utils/memberId';

type OrgGroup = {
  id: string;
  name: string;
  description: string;
};

type MemberIdVariants = {
  raw: string;
  normalized: string;
  prefixed: string;
};

const getMemberIdVariants = (id: string): MemberIdVariants => ({
  raw: id,
  normalized: normalizeMemberId(id),
  prefixed: toPrefixedMemberId(id),
});

const getGroupsForMember = (
  map: Map<string, IMemberGroup[]>,
  memberIdVariants: MemberIdVariants,
) =>
  map.get(memberIdVariants.raw) ??
  map.get(memberIdVariants.normalized) ??
  map.get(memberIdVariants.prefixed) ??
  [];

const identity = identityConfig(env);

const mergeGroupsById = (a: IMemberGroup[], b: IMemberGroup[]) => {
  if (a.length === 0) return b;
  if (b.length === 0) return a;
  const map = new Map<string, IMemberGroup>();
  a.forEach((g) => map.set(g.id, g));
  b.forEach((g) => {
    const existing = map.get(g.id);
    // Prefer a non-empty name when merging.
    if (!existing || (!existing.name && g.name)) {
      map.set(g.id, g);
    }
  });
  return Array.from(map.values());
};

const memberIdMatches = (targetMemberId: string, candidateMemberId: string) => {
  const target = getMemberIdVariants(targetMemberId);
  const candidate = getMemberIdVariants(candidateMemberId);
  return (
    candidate.raw === target.raw ||
    candidate.normalized === target.normalized ||
    candidate.prefixed === target.prefixed
  );
};

export const useMemberTeams = (
  memberId: string,
  memberGroups?: IMemberGroup[],
  refetchKey?: number,
  organizationId?: string,
  memberEmail?: string,
  isProfileReady?: boolean,
) => {
  const axiosInstance = useAuthenticatedAxios();
  const orgGroupsQuery = useOrgGroups();
  const isDetailsPage = refetchKey !== undefined;
  const allGroups = useMemo(
    () => (orgGroupsQuery.data?.data ?? []) as OrgGroup[],
    [orgGroupsQuery.data],
  );

  const [resolvedGroups, setResolvedGroups] = useState<IMemberGroup[]>(
    memberGroups ?? [],
  );
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [hasResolvedOnce, setHasResolvedOnce] = useState<boolean>(
    (memberGroups?.length ?? 0) > 0,
  );

  const lastMemberIdRef = useRef<string>('');

  const persistGroupsToCache = useCallback(
    (groups: IMemberGroup[]) => {
      const cacheKey = organizationId || 'default';
      try {
        const map =
          loadGroupsFromCache(cacheKey) ?? new Map<string, IMemberGroup[]>();
        const memberIdVariants = getMemberIdVariants(memberId);

        const apply = (key: string) => {
          const existing = map.get(key) ?? [];
          map.set(key, mergeGroupsById(existing, groups));
        };

        apply(memberIdVariants.raw);
        apply(memberIdVariants.normalized);
        apply(memberIdVariants.prefixed);

        saveGroupsToCache(map, cacheKey);
      } catch {
        // Ignore cache failures
      }
    },
    [memberId, organizationId],
  );

  // Keep details page in sync with add/remove mutations which update the shared
  // localStorage cache and dispatch a global event.
  useEffect(() => {
    if (!memberId) return;

    const cacheKey = organizationId || 'default';
    const memberIdVariants = getMemberIdVariants(memberId);

    const handleCacheCleared = (event: Event) => {
      const customEvent = event as CustomEvent<{
        organizationId?: string;
        memberId?: string;
        addedTeamIds?: string[];
        removedTeamId?: string;
      }>;
      const eventKey = customEvent.detail?.organizationId || 'default';
      if (eventKey !== cacheKey) return;

      const eventMemberId = customEvent.detail?.memberId;
      if (eventMemberId && !memberIdMatches(memberId, eventMemberId)) return;

      const addedTeamIds = customEvent.detail?.addedTeamIds ?? [];
      const removedTeamId = customEvent.detail?.removedTeamId;

      // If the event tells us exactly what changed, apply it directly to avoid
      // replacing the current list with a partial cache entry.
      if (addedTeamIds.length > 0) {
        const groupsMap = new Map(allGroups.map((g) => [g.id, g]));
        setResolvedGroups((prev) => {
          const next = [...prev];
          const existingIds = new Set(next.map((g) => g.id));
          addedTeamIds.forEach((id) => {
            if (existingIds.has(id)) return;
            const full = groupsMap.get(id);
            next.push({ id, name: full?.name ?? '' });
          });
          return next;
        });
        setHasResolvedOnce(true);
        setLoadingGroups(false);
        return;
      }

      if (removedTeamId) {
        setResolvedGroups((prev) => prev.filter((g) => g.id !== removedTeamId));
        setHasResolvedOnce(true);
        setLoadingGroups(false);
        return;
      }

      const cached = loadGroupsFromCache(cacheKey);
      const fromCache =
        (cached && getGroupsForMember(cached, memberIdVariants)) || undefined;

      if (!fromCache) return;

      // Merge cache into existing state so a partial cache entry cannot temporarily
      // replace the full list and cause flicker.
      setResolvedGroups((prev) => {
        if (fromCache.length === 0 && prev.length > 0) return prev;
        return mergeGroupsById(prev, fromCache);
      });
      setHasResolvedOnce(true);
      setLoadingGroups(false);
    };

    window.addEventListener('memberGroupsCacheCleared', handleCacheCleared);
    return () => {
      window.removeEventListener(
        'memberGroupsCacheCleared',
        handleCacheCleared,
      );
    };
  }, [memberId, organizationId, allGroups]);

  const optimisticPendingRef = useRef<{
    added: Set<string>;
    removed: Set<string>;
    expiresAt: number;
    attempts: number;
  } | null>(null);
  const retryTimeoutRef = useRef<number | null>(null);
  const [retryKey, setRetryKey] = useState(0);

  const clearRetryTimer = () => {
    if (retryTimeoutRef.current) {
      window.clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      clearRetryTimer();
    };
  }, []);

  const shouldAcceptFetchedGroups = useCallback((groups: IMemberGroup[]) => {
    const pending = optimisticPendingRef.current;
    if (!pending) return true;

    // Pending window expired: stop enforcing.
    if (Date.now() > pending.expiresAt) {
      optimisticPendingRef.current = null;
      return true;
    }

    const fetchedIds = new Set(groups.map((g) => g.id));
    // Added teams must be present; removed teams must be absent.
    for (const id of pending.added) {
      if (!fetchedIds.has(id)) return false;
    }
    for (const id of pending.removed) {
      if (fetchedIds.has(id)) return false;
    }

    // Server now reflects the change; clear pending.
    optimisticPendingRef.current = null;
    return true;
  }, []);

  const scheduleRetryIfPending = useCallback(() => {
    const pending = optimisticPendingRef.current;
    if (!pending) return;
    if (Date.now() > pending.expiresAt) {
      optimisticPendingRef.current = null;
      return;
    }
    if (pending.attempts >= 6) return;

    pending.attempts += 1;
    clearRetryTimer();
    retryTimeoutRef.current = window.setTimeout(() => {
      setRetryKey((v) => v + 1);
      retryTimeoutRef.current = null;
    }, 1000);
  }, []);

  // Prefer provided memberGroups; otherwise hydrate from cache or REST map
  useEffect(() => {
    if (!memberId) return;

    const memberIdVariants = getMemberIdVariants(memberId);

    // When switching between members, reset the "resolved" marker so we don't show a false empty state.
    if (lastMemberIdRef.current !== memberId) {
      lastMemberIdRef.current = memberId;
      const provided = (memberGroups?.length ?? 0) > 0;
      setResolvedGroups(memberGroups ?? []);
      setHasResolvedOnce(provided);
    }

    // If caller provided groups, use them directly
    if (memberGroups && memberGroups.length > 0) {
      setResolvedGroups(memberGroups);
      setHasResolvedOnce(true);
      return;
    }

    // Consult local cache first for immediate UI (add/remove flows update this cache),
    // but still fetch authoritative teams in the background so cached/partial data
    // doesn't permanently hide teams.
    const cached = loadGroupsFromCache(organizationId || 'default');
    const fromCache = cached && getGroupsForMember(cached, memberIdVariants);
    if (fromCache && fromCache.length > 0) {
      // Only use non-empty cache values for initial rendering.
      // An empty cached array is often a stale/partial entry and causes a flash of
      // the "no teams" message before the real fetch completes.
      setResolvedGroups((prev) => mergeGroupsById(prev, fromCache));
      setHasResolvedOnce(true);
      // Do not return; continue to REST/crawl fetch below.
    }

    // Details page: if we don't have the member email yet, don't attempt to resolve teams.
    // Without email, we often fall back to the group-member crawl too early and temporarily
    // conclude there are no teams, which causes the empty-state message to flash.
    // Once the member profile query has settled without an email (partial GraphQL
    // responses), stop waiting and resolve via the member-id crawl instead.
    const trimmedEmail = (memberEmail ?? '').trim();
    if (isDetailsPage && !trimmedEmail && !hasResolvedOnce) {
      if (!isProfileReady) {
        setLoadingGroups(true);
        return;
      }
    }

    // Prefer the same REST source the members list uses (includeGroups=true).
    // This avoids fragile ID mismatches between route params and group-member crawl responses.
    if (trimmedEmail && axiosInstance) {
      const abortController = new AbortController();
      let isMounted = true;

      setLoadingGroups(true);

      const runCrawlFallback = async () => {
        // If we're not on the details page (no refetchKey), we can't crawl anyway; mark as resolved.
        if (!isDetailsPage) {
          if (isMounted) {
            setHasResolvedOnce(true);
            setLoadingGroups(false);
          }
          return;
        }

        // Details page: if required inputs aren't ready yet, keep showing the loading skeleton.
        if (!organizationId || !axiosInstance) {
          if (isMounted) {
            setLoadingGroups(true);
          }
          return;
        }

        try {
          const map = await fetchMemberGroups(axiosInstance, organizationId);
          if (!isMounted || abortController.signal.aborted) return;
          const groups = getGroupsForMember(map, memberIdVariants);
          if (shouldAcceptFetchedGroups(groups)) {
            setResolvedGroups(groups);
            if (organizationId) persistGroupsToCache(groups);
          } else {
            scheduleRetryIfPending();
          }
        } catch {
          // Ignore; we'll just show no teams.
        } finally {
          if (isMounted) {
            setHasResolvedOnce(true);
            setLoadingGroups(false);
          }
        }
      };

      const baseUrl = identity.get_members.url(1, 25, trimmedEmail);
      const urlWithGroups = `${baseUrl}${
        baseUrl.includes('?') ? '&' : '?'
      }includeGroups=true`;

      axiosInstance
        .get<IGetMembersResponse>(urlWithGroups, {
          signal: abortController.signal,
        })
        .then((response) => {
          if (!isMounted || abortController.signal.aborted) return;

          const normalizedTargetId = memberIdVariants.normalized;
          const prefixedTargetId = memberIdVariants.prefixed;
          const targetEmail = trimmedEmail.toLowerCase();

          const match = (response.data?.data ?? []).find((m) => {
            const mId = m?.id ?? '';
            const mEmail = (m?.email ?? '').toLowerCase();
            const normalizedMId = normalizeMemberId(mId);
            const prefixedMId = toPrefixedMemberId(mId);

            return (
              (mEmail && mEmail === targetEmail) ||
              mId === memberId ||
              normalizedMId === normalizedTargetId ||
              prefixedMId === prefixedTargetId
            );
          });

          const groups = match?.groups ?? [];
          if (groups.length > 0) {
            if (shouldAcceptFetchedGroups(groups)) {
              setResolvedGroups(groups);
              if (organizationId) persistGroupsToCache(groups);
            } else {
              scheduleRetryIfPending();
            }
            setHasResolvedOnce(true);
            setLoadingGroups(false);
            return;
          }

          // Email search returned no groups; fall back to crawl if allowed.
          return runCrawlFallback();
        })
        .catch(() => {
          if (!isMounted) return;
          // REST lookup failed; fall back to crawl if allowed.
          return runCrawlFallback();
        })
        .finally(() => {
          // If REST succeeded (with groups) we already stopped loading.
          // If REST failed and crawl fallback ran, it manages loading+ready.
          if (!isMounted) return;
          // Details page: if we still don't have the inputs needed to resolve teams,
          // don't mark the teams as resolved yet (avoids showing the empty state).
          if (isDetailsPage && (!organizationId || !axiosInstance)) {
            return;
          }
          setHasResolvedOnce(true);
        });

      return () => {
        isMounted = false;
        abortController.abort();
      };
    }

    // Fetch fresh map when refetchKey is provided (details page) and org is known
    if (!isDetailsPage) {
      setHasResolvedOnce(true);
      return;
    }

    // Details page: if required inputs aren't ready yet, keep showing the loading skeleton.
    if (!organizationId || !axiosInstance) {
      setLoadingGroups(true);
      return;
    }

    let isMounted = true;
    setLoadingGroups(true);
    fetchMemberGroups(axiosInstance, organizationId)
      .then((map) => {
        if (!isMounted) return;
        const groups = getGroupsForMember(map, memberIdVariants);
        if (shouldAcceptFetchedGroups(groups)) {
          setResolvedGroups(groups);
          if (organizationId) persistGroupsToCache(groups);
        } else {
          scheduleRetryIfPending();
        }
        setHasResolvedOnce(true);
      })
      .catch(() => {
        if (isMounted) {
          setHasResolvedOnce(true);
        }
      })
      .finally(() => {
        if (isMounted) setLoadingGroups(false);
      });

    return () => {
      isMounted = false;
    };
  }, [
    memberId,
    memberGroups,
    refetchKey,
    organizationId,
    axiosInstance,
    memberEmail,
    isProfileReady,
    hasResolvedOnce,
    isDetailsPage,
    shouldAcceptFetchedGroups,
    scheduleRetryIfPending,
    persistGroupsToCache,
    retryKey,
  ]);

  const groupsToUse = useMemo(() => resolvedGroups, [resolvedGroups]);

  const teams: IMemberTeam[] = useMemo(() => {
    if (!memberId) {
      return [];
    }

    if (groupsToUse.length === 0) {
      return [];
    }

    const groupsMap = new Map(allGroups.map((group) => [group.id, group]));

    return groupsToUse.map((memberGroup) => {
      const fullGroup = groupsMap.get(memberGroup.id);

      return {
        id: memberGroup.id,
        name: memberGroup.name || fullGroup?.name || '',
        description: fullGroup?.description || '',
        accessLevel: undefined,
      };
    });
  }, [memberId, groupsToUse, allGroups]);

  // Only show the skeleton until we have attempted to resolve teams at least once.
  // After that, keep showing the current list (including optimistic state) while background refetches run.
  const shouldShowInitialSkeleton =
    isDetailsPage && !hasResolvedOnce && resolvedGroups.length === 0;
  const isLoadingForDetails =
    orgGroupsQuery.isLoading ||
    shouldShowInitialSkeleton ||
    (resolvedGroups.length === 0 && loadingGroups);

  const optimisticallyAddTeams = useCallback(
    (teamIds: string[]) => {
      if (!teamIds || teamIds.length === 0) return;

      const now = Date.now();
      const pending = optimisticPendingRef.current;
      const nextPending =
        pending && now <= pending.expiresAt
          ? pending
          : {
              added: new Set<string>(),
              removed: new Set<string>(),
              expiresAt: now + 15000,
              attempts: 0,
            };
      teamIds.forEach((id) => {
        nextPending.removed.delete(id);
        nextPending.added.add(id);
      });
      optimisticPendingRef.current = nextPending;

      const groupsMap = new Map(allGroups.map((group) => [group.id, group]));
      setResolvedGroups((prev) => {
        const existingIds = new Set(prev.map((g) => g.id));
        const toAdd: IMemberGroup[] = [];
        teamIds.forEach((id) => {
          if (existingIds.has(id)) return;
          const full = groupsMap.get(id);
          toAdd.push({ id, name: full?.name ?? '' });
        });
        return toAdd.length ? [...prev, ...toAdd] : prev;
      });
      setHasResolvedOnce(true);
      setLoadingGroups(false);
    },
    [allGroups],
  );

  const optimisticallyRemoveTeam = useCallback((teamId: string) => {
    if (!teamId) return;

    const now = Date.now();
    const pending = optimisticPendingRef.current;
    const nextPending =
      pending && now <= pending.expiresAt
        ? pending
        : {
            added: new Set<string>(),
            removed: new Set<string>(),
            expiresAt: now + 15000,
            attempts: 0,
          };
    nextPending.added.delete(teamId);
    nextPending.removed.add(teamId);
    optimisticPendingRef.current = nextPending;

    setResolvedGroups((prev) => prev.filter((g) => g.id !== teamId));
    setHasResolvedOnce(true);
    setLoadingGroups(false);
  }, []);

  const refetchTeams = () => {
    if (!isDetailsPage) return;
    if (!organizationId || !axiosInstance) {
      return;
    }
    setLoadingGroups(true);
    fetchMemberGroups(axiosInstance, organizationId)
      .then((map) => {
        const groups = getGroupsForMember(map, getMemberIdVariants(memberId));
        setResolvedGroups(groups);
        if (organizationId) persistGroupsToCache(groups);
        setHasResolvedOnce(true);
      })
      .catch(() => setHasResolvedOnce(true))
      .finally(() => setLoadingGroups(false));
  };

  return {
    teams,
    isLoading: isLoadingForDetails,
    hasResolvedTeams: hasResolvedOnce,
    refetchTeams,
    optimisticallyAddTeams,
    optimisticallyRemoveTeam,
  };
};
